import { DesignToken } from "@livechat/design-system-react-components";

import { StatusTag } from "components/status-tag/StatusTag";
import { ReportDistribution } from "constants/reports/distribution";
import { TicketStatus } from "interfaces/entities/ticket";
import { ISolvedAndClosedStats, ITicketsStatusStats } from "services/api/report/interfaces";
import { withVar } from "themes/helpers";

import { getFormatedPercent, getLabelsFromPeriod, getSeriesDataFromPeriod, sumValueFromPeriod } from "../helpers";
import { ISolvedClosedTicketsData, ISolvedClosedTicketsStats } from "../interfaces";

const solvedTicketsColor = withVar(DesignToken.DecorGreen500);
const closedTicketsColor = withVar(DesignToken.DecorGray500);

function mapTicketsToArray(
  closedTickets: ITicketsStatusStats,
  solvedTickets: ITicketsStatusStats
): Array<ISolvedAndClosedStats> {
  const allIds = new Set([...Object.keys(closedTickets), ...Object.keys(solvedTickets)]);

  return Array.from(allIds).map(id => ({
    id,
    solved: solvedTickets[id]?.count || 0,
    closed: closedTickets[id]?.count || 0,
    total: (solvedTickets[id]?.count || 0) + (closedTickets[id]?.count || 0),
  }));
}

export function solvedClosedTicketsDeserializer(
  { data }: { data: ISolvedClosedTicketsData },
  distribution: ReportDistribution
): ISolvedClosedTicketsStats {
  const solvedTickets = getSeriesDataFromPeriod(data.solvedTickets, "count", distribution);
  const solvedTicketsSummary = sumValueFromPeriod(data.solvedTickets, "count", distribution);
  const closedTickets = getSeriesDataFromPeriod(data.closedTickets, "count", distribution);
  const closedTicketsSummary = sumValueFromPeriod(data.closedTickets, "count", distribution);
  const solvedClosedTicketsSummary = solvedTicketsSummary + closedTicketsSummary;
  const labels = getLabelsFromPeriod(data.solvedTickets, distribution);
  const { closedTicketsPerTeam, solvedTicketsPerTeam, closedTicketsPerAgent, solvedTicketsPerAgent } = data;
  const solvedAndClosedTicketsPerTeam =
    data.closedTicketsPerTeam && data.solvedTicketsPerTeam
      ? mapTicketsToArray(closedTicketsPerTeam, solvedTicketsPerTeam)
      : [];
  const solvedAndClosedTicketsPerAgent =
    data.closedTicketsPerAgent && data.solvedTicketsPerAgent
      ? mapTicketsToArray(closedTicketsPerAgent, solvedTicketsPerAgent)
      : [];
  const teamsCount = new Set(solvedAndClosedTicketsPerTeam.map(item => item.id)).size;
  const agentsCount = new Set(solvedAndClosedTicketsPerAgent.map(item => item.id)).size;
  const solvedPerTeamAverage = parseInt((solvedTicketsSummary / teamsCount).toFixed(0));
  const closedPerTeamAverage = parseInt((closedTicketsSummary / teamsCount).toFixed(0));
  const solvedClosedPerTeamAverage = parseInt((solvedClosedTicketsSummary / teamsCount).toFixed(0));

  const solvedPerAgentAverage = parseInt((solvedTicketsSummary / agentsCount).toFixed(0));
  const closedPerAgentAverage = parseInt((closedTicketsSummary / agentsCount).toFixed(0));
  const solvedClosedPerAgentAverage = parseInt((solvedClosedTicketsSummary / agentsCount).toFixed(0));

  const perTeamTotal = {
    id: "total",
    solved: isNaN(solvedPerTeamAverage) ? 0 : solvedPerTeamAverage,
    closed: isNaN(closedPerTeamAverage) ? 0 : closedPerTeamAverage,
    total: isNaN(solvedClosedPerTeamAverage) ? 0 : solvedClosedPerTeamAverage,
  };

  const perAgentTotal = {
    id: "total",
    solved: isNaN(solvedPerAgentAverage) ? 0 : solvedPerAgentAverage,
    closed: isNaN(closedPerAgentAverage) ? 0 : closedPerAgentAverage,
    total: isNaN(solvedClosedPerAgentAverage) ? 0 : solvedClosedPerAgentAverage,
  };

  return {
    solvedAndClosedTicketsPerTeam: [perTeamTotal, ...solvedAndClosedTicketsPerTeam],
    solvedAndClosedTicketsPerAgent: [perAgentTotal, ...solvedAndClosedTicketsPerAgent],
    solvedClosedTickets: {
      summary: [
        {
          name: "Overall",
          value: solvedClosedTicketsSummary,
        },
        {
          name: "Solved",
          value: solvedTicketsSummary,
          color: solvedTicketsColor,
          label: `(${getFormatedPercent(solvedTicketsSummary / solvedClosedTicketsSummary)})`,
        },
        {
          name: "Closed",
          value: closedTicketsSummary,
          color: closedTicketsColor,
          label: `(${getFormatedPercent(closedTicketsSummary / solvedClosedTicketsSummary)})`,
        },
      ],
      labels,
      series: [
        {
          name: "solved tickets",
          color: solvedTicketsColor,
          data: solvedTickets,
          extras: {
            breakdownCustomElement: (
              <div>
                <StatusTag status={TicketStatus.Solved} />
              </div>
            ),
          },
        },
        {
          name: "closed tickets",
          color: closedTicketsColor,
          data: closedTickets,
          extras: {
            breakdownCustomElement: (
              <div>
                <StatusTag status={TicketStatus.Closed} />
              </div>
            ),
          },
        },
      ],
    },
  };
}
